import type { ChangeEvent, ReactElement } from 'react';
import {
    Button,
    ButtonRow,
    Card,
    FormGroup,
    FormLabel,
    FormSection,
    Radio,
    RadioGroup,
    TextArea,
} from '@ksyos/design-system';
import { connect } from 'react-redux';
import type { IRequest } from '../../../../../common/interfaces/IRequest';
import type { IGtoProcedureCustomControlData } from '../../../../../common/validation/customControls/general';
import type { IReduxState } from '../../../../configureStore';
import type { IStepProps } from '../NewGhcRequest';
import { ServiceCode } from '../../../../../common/enums/ServiceCode';
import { translateKey } from '../../../../translate';
import { isPendingRequestForService } from '../../../../utils/isPendingRequestForService';
import { Content } from '../../../elements/molecules/content/Content';

const radioOptions = ['Ja', 'Nee'];

interface IStep3ReduxProps {
    pendingGtoRequest?: IRequest;
}

const mapStateToProps = ({ authentication }: IReduxState): IStep3ReduxProps => ({
    pendingGtoRequest: authentication.requestProcesses?.find((request: IRequest) =>
        isPendingRequestForService(request, ServiceCode.GTO)
    ),
});

function Step3Wrapper(props: IStepProps & IStep3ReduxProps) {
    return (
        <Card.Body>
            <FormSection>
                <FormGroup
                    hasErrors={props.errors.has('areThereAdditionalComplaintsOrSymptons')}
                    label="Zijn er bijkomende klachten of symptomen?"
                >
                    {jaNeeElements(
                        'areThereAdditionalComplaintsOrSymptons',
                        'areThereAdditionalComplaintsOrSymptonsRemark',
                        'Zo ja, welke zijn dit?',
                        'Bijkomende klachten...'
                    )}
                </FormGroup>

                <FormGroup
                    hasErrors={props.errors.has('doYouHaveFever')}
                    label="Heb je koorts?"
                >
                    {jaNeeElements(
                        'doYouHaveFever',
                        'doYouHaveFeverRemark',
                        'Zo ja, hoe heb je dit gemeten?',
                        'Ik heb dit gemeten...'
                    )}
                </FormGroup>

                <FormGroup
                    hasErrors={props.errors.has('haveYouEverBeenHospitalized')}
                    label="Ben je wel eens in het ziekenhuis opgenomen?"
                >
                    {jaNeeElements(
                        'haveYouEverBeenHospitalized',
                        'haveYouEverBeenHospitalizedRemark',
                        'Zo ja, waarvoor?',
                        'Ik ben opgenomen wegens...'
                    )}
                </FormGroup>

                {!props.pendingGtoRequest && (
                    <>
                        <FormGroup
                            hasErrors={props.errors.has('areYouBeingTreated')}
                            label="Ben je onder behandeling door specialist of huisarts?"
                        >
                            {jaNeeElements(
                                'areYouBeingTreated',
                                'areYouBeingTreatedRemark',
                                'Zo ja, waarvoor?',
                                'Ik ben opgenomen wegens...'
                            )}
                        </FormGroup>

                        <FormGroup
                            hasErrors={props.errors.has('areYouOnMedication')}
                            label="Gebruik je medicijnen, pijnstillers of de pil?"
                        >
                            {jaNeeElements(
                                'areYouOnMedication',
                                'areYouOnMedicationRemark',
                                'Zo ja, welke en hoeveel?',
                                'Ik gebruik 1 maal daags 3mg medicijn...'
                            )}
                        </FormGroup>
                    </>
                )}
            </FormSection>
            <FormSection isBorderless>
                <ButtonRow justify="center">
                    <Button
                        onClick={props.validateAndGoToNextStep}
                        type="button"
                        variant="solid"
                    >
                        Volgende
                    </Button>
                </ButtonRow>
            </FormSection>
        </Card.Body>
    );

    function jaNeeElements(
        customControlKey: keyof IGtoProcedureCustomControlData,
        customControlKeyRemark: keyof IGtoProcedureCustomControlData,
        title: string,
        placeholder: string
    ): ReactElement {
        return (
            <>
                <RadioGroup
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const updatedCustomControlData = { ...props.request.customControlData };
                        (updatedCustomControlData[customControlKey] as string) = event.target.value;
                        if (event.target.value === 'Nee') {
                            (updatedCustomControlData[customControlKeyRemark] as string) = '';
                        }
                        props.handleChange(updatedCustomControlData);
                    }}
                    value={(props.request.customControlData[customControlKey] as string) ?? ''}
                >
                    {radioOptions.map((option) => (
                        <Radio
                            id={customControlKey + option}
                            key={customControlKey + option}
                            label={option}
                            value={option}
                        />
                    ))}
                </RadioGroup>
                {props.request.customControlData[customControlKey] === 'Ja' && (
                    <Content className="mt-3">
                        <FormLabel>{title}</FormLabel>
                        <TextArea
                            error={
                                props.errors.get(customControlKeyRemark)
                                    ? translateKey('validation.' + props.errors.get(customControlKeyRemark))
                                    : undefined
                            }
                            onChange={(e) => props.handleChange({ [customControlKeyRemark]: e.target.value })}
                            placeholder={placeholder}
                            value={(props.request.customControlData[customControlKeyRemark] as string) || ''}
                        />
                    </Content>
                )}
            </>
        );
    }
}

export const Step3 = connect(mapStateToProps)(Step3Wrapper);
